import React, { useEffect, useState } from "react";
import { formatInYardTimeZone } from "../../../utils/dateUtils";
import { YardDateField } from "../../../components/common/YardDateField";
import jsonExport from "jsonexport/dist";
import { fillDownRows } from "../../../utils/exportsUtils";

import { Card, Divider, Tab, Tabs, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  CreateButton,
  Datagrid,
  DateInput,
  downloadCSV,
  EditButton,
  FilterButton,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  RefreshButton,
  ShowButton,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useListContext,
} from "react-admin";

import { ChevronRightIcon } from "../../../components/common/iconography";
import { BetaBadge } from "../../../components/common/FeatureBadges";
import ExportButton from "../../../components/common/ExportButton";
import { Empty } from "../../../components/common/Empty";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { SearchField } from "../../../components/common/SearchField";
import { Pagination } from "../../../components/Pagination";
import { QuickFilter } from "../../../components/common/QuickFilter";

import { useSearchableList } from "../../../dataProvider/useSearchableList";
import { isAuthorized } from "../../../utils/appUtils";
import { AclType, InboundStatus } from "../../../utils/constants";
import {
  exceptionRenderer,
  exportCustomField,
  normalizeValue,
} from "../../../utils/utils";
import { InboundStateIcon } from "../../../managerApp/resources/inboundShipments/InboundStateIcon";
import { AdviceStatusIcon } from "./AdviceStatusIcon";
import { useUser } from "../../../authenticatorApp/UserContext";

const useStyles = makeStyles((theme) => ({
  listRoot: {
    transition: theme.transitions.create(["all"], {
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const tabs = [
  {
    id: InboundStatus.ADVICE_ALL,
    name: (
      <span>
        Advices <BetaBadge />
      </span>
    ),
    sortKey: "scheduledDateTime",
    sortOrder: "ASC",
    showSmall: false,
  },
  {
    id: InboundStatus.IN_TRANSIT,
    name: "In Transit",
    sortKey: "scheduledDateTime",
    sortOrder: "ASC",
    showSmall: true,
  },
  {
    id: InboundStatus.ARRIVED,
    name: "Notified",
    sortKey: "notifyDateTime",
    sortOrder: "ASC",
    showSmall: true,
  },
  {
    id: InboundStatus.PLACED,
    name: "PLACED",
    sortKey: "placedDateTime",
    sortOrder: "ASC",
    showSmall: true,
  },
  {
    id: InboundStatus.RELEASED,
    name: "Released",
    sortKey: "releaseDateTime",
    sortOrder: "DESC",
    showSmall: false,
  },
];

const getVisibleTabs = (mdDown) => {
  return tabs.filter(
    (tab) => isTabEnabled(tab.id) && (!mdDown || tab.showSmall),
  );
};

// Inbound advice uses an AI-driven form (doc upload + load/carrier type), so
// unlike outbound there are no per-input field definitions to configure and no
// Account Settings section to populate. Gate purely on the ACL permission.
const isAdviceEnabled = () => {
  return isAuthorized(AclType.INBOUND_ADVICE);
};

const isTabEnabled = (tabId) => {
  if (tabId === InboundStatus.ADVICE_ALL) return isAdviceEnabled();
  return true;
};

// Rejected advices are editable so the customer can correct and resubmit;
// everything else is read-only (show).
const getRowClickAction = (record) =>
  record.status === InboundStatus.ADVICE_REJECTED ? "edit" : "show";

const renderActionButtons = (record) =>
  record.status === InboundStatus.ADVICE_REJECTED ? (
    <EditButton record={record} />
  ) : (
    <ShowButton record={record} />
  );

export const exporter = (inbounds) => {
  const customHeaderMap = new Map();
  debugger;

  const inboundsForExport = inbounds.map((inbound) => {
    const inboundForExport = { ...inbound };

    if (inbound.inventories?.items) {
      inboundForExport.tallySheet = inbound.inventories.items.map(
        (inventory) => {
          const lineItem = {
            inventoryId: inventory.id,
            productCode: inventory.productCode,
            productName: inventory.productName,
            lotNumber: inbound.lotNumber,
            dateReceived: inbound.placedDateTime
              ? formatInYardTimeZone(
                  new Date(inbound.placedDateTime),
                  "MM/dd/yyyy",
                )
              : "",
            quantityReceived: inventory.quantityReceived,
            units: inbound.totalUOM,
            pieces: inbound.pcs,
          };

          return lineItem;
        },
      );
    }
    delete inboundForExport.inventories;

    if (inbound.customFields?.length > 0) {
      debugger;
      inbound.customFields.forEach((field) => {
        exportCustomField(field, inboundForExport, customHeaderMap);
      });
    }

    delete inboundForExport.account;
    delete inboundForExport.accountId;
    delete inboundForExport.area;
    delete inboundForExport.areaId;
    delete inboundForExport.assignedTo;
    delete inboundForExport.carrierType;
    delete inboundForExport.createdAt;
    delete inboundForExport.createDateTime;
    delete inboundForExport.daysOnDemurrageClock;
    delete inboundForExport.endDateTime;
    delete inboundForExport.groups;
    delete inboundForExport.history;
    delete inboundForExport.invoiceId;
    delete inboundForExport.notes;
    delete inboundForExport.owner;
    delete inboundForExport.attachments;
    delete inboundForExport.photos;
    delete inboundForExport.reloadYardCommodity;
    delete inboundForExport.reloadYardCommodityId;
    delete inboundForExport.reloadYardId;
    delete inboundForExport.seals;
    delete inboundForExport.startDateTime;
    delete inboundForExport.state;
    delete inboundForExport.stripeBillingDateTime;
    delete inboundForExport.updatedAt;
    delete inboundForExport.verificationRequired;
    delete inboundForExport.zone;
    delete inboundForExport.zoneId;

    return inboundForExport;
  });

  jsonExport(
    fillDownRows(inboundsForExport),
    { rename: ["inboundShipmentId"] },
    (err, csv) => {
      downloadCSV(csv, `inboundShipments_${new Date().toLocaleDateString()}`); // download as 'inboundShipments.csv` file
    },
  );
};

const InboundSearchField = (props) => {
  const { label, alwaysOn } = props;
  const { displayedFilters, filterValues, setFilters } = useListContext(props);

  const handleSearch = (value) => {
    debugger;
    if (value) {
      const normalizedValue = normalizeValue(value);
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(
        {
          ...nextFilterValues,
          or: [
            { carrierName: { matchPhrasePrefix: `${value}` } },
            { shipFrom: { matchPhrasePrefix: `${value}` } },
            // { normalizedPoNumber: { matchPhrasePrefix: `${normalizedValue}` } },
            // { normalizedRailCarNumber: { matchPhrasePrefix: `${normalizedValue}` } },
            { normalizedPoNumber: { wildcard: `*${normalizedValue}*` } },
            { normalizedRailCarNumber: { wildcard: `*${normalizedValue}*` } },
            { lotNumber: { matchPhrasePrefix: `${value}` } },
            { lotNumber: { wildcard: `*${value}*` } },
            { customFields: { matchPhrasePrefix: `${value}` } },
            { customFields: { wildcard: `*${value}*` } },
          ],
        },
        displayedFilters,
      );
    } else {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(nextFilterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = (visibleTabs, tabIndex) => {
  const filters = [<InboundSearchField label="Search" alwaysOn />];

  if (visibleTabs[tabIndex].id === InboundStatus.RELEASED) {
    filters.push(
      <DateInput
        source="releaseDateTime.lte"
        label="Released Before..."
        variant="outlined"
        resettable
        onKeyPress={(event) => event.preventDefault()}
      />,
      <DateInput
        source="releaseDateTime.gte"
        label="Released After..."
        variant="outlined"
        resettable
        onKeyPress={(event) => event.preventDefault()}
      />,
      <QuickFilter source="sit" label="SIT" defaultValue={{ eq: true }} />,
    );
  }
  return filters;
};

const ListActions = (props) => {
  const { xsDown } = props;
  const { resource, total } = useListContext();

  return !xsDown ? (
    <TopToolbar>
      <FilterButton />
      {isAdviceEnabled() && <CreateButton />}
      <ExportButton
        disabled={total === 0}
        resource={resource}
        exporter={exporter}
      />
      <RefreshButton />
    </TopToolbar>
  ) : null;
};

const getSortOrder = (visibleTabs, tabIndex) => {
  return visibleTabs[tabIndex].sortOrder;
};

const getSortBy = (visibleTabs, tabValue) => {
  const tabIndex = visibleTabs.findIndex((tab) => tab.id === tabValue);
  return `{"key":"inboundShipmentByAccountByStatusByDates", "search":"${visibleTabs[tabIndex].sortKey}"}`;
};

const getSessionTab = (visibleTabs, appUser) => {
  const sessionTab = Number(sessionStorage.getItem(`InboundTab_${appUser.id}`));
  if (sessionTab && visibleTabs[sessionTab]) {
    return sessionTab;
  }
  return 0; // Default to the first visible tab if the sessionTab is not valid
};

export const InboundList = (props) => {
  const { appUser } = useUser();
  const classes = useStyles();
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const mdDown = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [visibleTabs, setVisibleTabs] = useState(getVisibleTabs(mdDown));
  const [tabIndex, setTabIndex] = useState(getSessionTab(visibleTabs, appUser));

  const [defaultFilter, setDefaultFilter] = useState({
    accountId: appUser.accountId,
    statusReleaseDateTimePlacedDateTimeNotifyDateTimeScheduledDateTimeCreateDateTime:
      {
        beginsWith: {
          status: visibleTabs[tabIndex].id,
        },
      },
  });
  const [defaultSearchFilter, setDefaultSearchFilter] = useState({
    reloadYardId: { eq: appUser?.reloadYardId },
    accountId: { eq: appUser.accountId },
    status: { eq: visibleTabs[tabIndex].id },
  });
  const defaultSort = {
    field: getSortBy(visibleTabs, visibleTabs[tabIndex].id),
    order: getSortOrder(visibleTabs, tabIndex),
  };

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const sessionPerPage = Number(
    localStorage.getItem(`${props.resource}_perPage`),
  );
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? sessionPerPage : 10,
  );

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Inbound",
    defaultFilter: defaultFilter,
    defaultSearchFilter: defaultSearchFilter,
    defaultPerPage: defaultPerPage,
    defaultSort: defaultSort,
    resource: "inboundShipments",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext?.perPage]);

  useEffect(() => {
    if (listContext?.filter) {
      // Extract the key query from defaultSort
      const fieldObj = JSON.parse(defaultSort.field);
      const keyToCheck = fieldObj.key;

      // Check for the key's presence in filter
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) listContext.setFilters({});
    }
  }, [listContext?.filter]);

  useEffect(() => {
    setVisibleTabs(getVisibleTabs(mdDown));
  }, [mdDown]);

  const handleTabChange = (value) => {
    debugger;
    setTabIndex(value);
    setDefaultFilter({
      accountId: appUser.accountId,
      statusReleaseDateTimePlacedDateTimeNotifyDateTimeScheduledDateTimeCreateDateTime:
        {
          beginsWith: {
            status: visibleTabs[value].id,
          },
        },
    });
    setDefaultSearchFilter({
      reloadYardId: { eq: appUser?.reloadYardId },
      accountId: { eq: appUser.accountId },
      status: { eq: visibleTabs[value].id },
    });
    listContext.setSort(
      getSortBy(visibleTabs, visibleTabs[value].id),
      getSortOrder(visibleTabs, value),
    );
    listContext.setPage(1);
  };

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Inbound" />} />
      <ListToolbar
        {...props}
        filters={getListFilters(visibleTabs, tabIndex)}
        actions={<ListActions xsDown={xsDown} />}
      />
      <Card className={classes.listRoot}>
        <ShipmentTabs
          {...props}
          {...listContext}
          appUser={appUser}
          visibleTabs={visibleTabs}
          tabIndex={tabIndex}
          setTabIndex={handleTabChange}
          xsDown={xsDown}
          isMedium={mdDown}
        />
        <Pagination {...listContext} />
      </Card>
    </ListContextProvider>
  );
};

const ShipmentTabs = ({ tabIndex, setTabIndex, setPage, ...props }) => {
  const { visibleTabs, appUser } = props;

  const handleChange = (event, value) => {
    const adjustedValue = visibleTabs[value] ? value : 0; // Adjust tab index to the first visible tab if needed
    sessionStorage.setItem(`InboundTab_${appUser.id}`, adjustedValue);
    setTabIndex(adjustedValue);
  };

  return (
    <>
      <Tabs
        value={Number(tabIndex)}
        indicatorColor="primary"
        textColor="primary"
        onChange={handleChange}
      >
        {visibleTabs.map((tab, idx) => (
          <Tab key={tab.id} label={tab.name} id={`shipment-${idx}`} />
        ))}
      </Tabs>
      <Divider />
      {visibleTabs.map(
        (tab) =>
          tab.id === visibleTabs[tabIndex].id && (
            <ShipmentGrid {...props} key={tab.id} tabValue={tab.id} />
          ),
      )}
    </>
  );
};

const ShipmentGrid = ({ ids, page, visibleTabs, tabValue, ...props }) => {
  const { xsDown, isMedium } = props;

  if (tabValue === InboundStatus.ADVICE_ALL && ids.length === 0 && page === 1)
    return <Empty {...props} message="Create inbound advice" />;
  return xsDown ? (
    <SimpleList
      linkType={(record) => getRowClickAction(record)}
      primaryText={(record) => record.accountName}
      secondaryText={(record) => `Car/Trl #:: ${record.railCarNumber}`}
      tertiaryText={(record) => `PO #: ${record.poNumber}`}
      leftIcon={(record) => <InboundStateIcon record={record} />}
      rightIcon={() => (
        <ChevronRightIcon
          fontSize="large"
          color="secondary"
          style={{ marginLeft: 40, marginTop: 10 }}
        />
      )}
    />
  ) : isMedium ? (
    <Datagrid
      rowClick={(jsonRecord, basePath, record) => getRowClickAction(record)}
    >
      {tabValue === InboundStatus.ADVICE_ALL ? <AdviceStatusIcon /> : null}
      {tabValue === InboundStatus.PLACED ||
      tabValue === InboundStatus.RELEASED ? (
        <InboundStateIcon />
      ) : null}
      {tabValue === InboundStatus.PLACED ||
      tabValue === InboundStatus.RELEASED ? (
        <FunctionField
          source="hasException"
          label=" "
          render={exceptionRenderer}
        />
      ) : null}
      {tabValue === InboundStatus.IN_TRANSIT ? (
        <YardDateField
          source="scheduledDateTime"
          label="Scheduled Time"
          showTime={true}
          sortable={true}
          sortBy={getSortBy(visibleTabs, tabValue)}
        />
      ) : null}
      {tabValue === InboundStatus.ARRIVED ? (
        <YardDateField
          source="notifyDateTime"
          label="Notify Date"
          sortable={true}
          sortBy={getSortBy(visibleTabs, tabValue)}
        />
      ) : null}
      {tabValue === InboundStatus.PLACED ? (
        <YardDateField
          source="placedDateTime"
          label="Placed Date"
          sortable={true}
          sortBy={getSortBy(visibleTabs, tabValue)}
        />
      ) : null}
      {tabValue === InboundStatus.RELEASED ? (
        <YardDateField
          source="releaseDateTime"
          label="Release Date"
          sortable={true}
          sortBy={getSortBy(visibleTabs, tabValue)}
        />
      ) : null}
      <TextField source="railCarNumber" label="Car/Trl No" sortable={true} />
      <TextField source="poNumber" label="PO Number" sortable={true} />
    </Datagrid>
  ) : (
    <Datagrid
      rowClick={(jsonRecord, basePath, record) => getRowClickAction(record)}
      hasBulkActions={false}
    >
      {tabValue === InboundStatus.ADVICE_ALL ? <AdviceStatusIcon /> : null}
      {tabValue === InboundStatus.PLACED ||
      tabValue === InboundStatus.RELEASED ? (
        <InboundStateIcon />
      ) : null}
      {tabValue === InboundStatus.PLACED ||
      tabValue === InboundStatus.RELEASED ? (
        <FunctionField
          source="hasException"
          label=" "
          render={exceptionRenderer}
        />
      ) : null}
      {tabValue === InboundStatus.IN_TRANSIT ? (
        <YardDateField
          source="scheduledDateTime"
          label="Scheduled Time"
          showTime={true}
          sortable={true}
          sortBy={getSortBy(visibleTabs, tabValue)}
        />
      ) : null}
      {tabValue === InboundStatus.ARRIVED ? (
        <YardDateField
          source="notifyDateTime"
          label="Notify Date"
          sortable={true}
          sortBy={getSortBy(visibleTabs, tabValue)}
        />
      ) : null}
      {tabValue === InboundStatus.PLACED ? (
        <YardDateField
          source="placedDateTime"
          label="Placed Date"
          sortable={true}
          sortBy={getSortBy(visibleTabs, tabValue)}
        />
      ) : null}
      {tabValue === InboundStatus.RELEASED ? (
        <YardDateField
          source="releaseDateTime"
          label="Release Date"
          sortable={true}
          sortBy={getSortBy(visibleTabs, tabValue)}
        />
      ) : null}
      <TextField source="railCarNumber" label="Car/Trl No" sortable={true} />
      <TextField source="poNumber" label="PO Number" sortable={true} />
      <FunctionField render={(record) => renderActionButtons(record)} />
    </Datagrid>
  );
};
